import { CountriesObject } from './countries.js';

if (!customElements.get('estimated-arrival')) {
  class EstimatedArrival extends HTMLElement {
    constructor() {
      super();
      this.variantData = null;
      this.rates = [];
      this.selectedRateIndex = 0;
      this.abortController = null;
    }

    connectedCallback() {
      if (this.dataset.productRequiresShipping === 'false') return;

      this.contentEl = this.querySelector('.estimated-arrival__content');
      this.variantData = this.getVariantData();
      // Postcode comes from the shopper (entered or stored). Country comes from the
      // Shopify market (data-country). We deliberately do NOT IP-geolocate the visitor:
      // sending `no_cities: true` made JSRates ignore this postcode and quote the
      // visitor's IP location, which never matches what the buyer types at checkout.
      this.postcode = sessionStorage.getItem('ea-postcode') || '';
      this.destCountry = this.dataset.country || 'AU';
      this.currency = this.dataset.currency || 'AUD';
      this.ratesUrl = this.dataset.ratesUrl || '/apps/jsrates/rates';
      this.ratesKey = this.dataset.ratesKey || '';
      this.shopDomain = this.dataset.shopDomain || '';

      const variantPicker = this.closest('.product-info')?.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      this.fetchRates();
    }

    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      if (!dataEl) return [];
      try {
        return JSON.parse(dataEl.textContent);
      } catch (e) {
        return [];
      }
    }

    buildPayload() {
      return {
        rate: {
          origin: {
            country: 'AU',
            postal_code: '2163',
            province: 'NSW',
            city: 'Villawood',
            name: null,
            address1: '9/2 Christina Rd',
            address2: '',
            address3: null,
            latitude: -33.8803172,
            longitude: 150.9896627,
            phone: '+61288809033',
            fax: null,
            email: null,
            address_type: null,
            company_name: 'Dulora'
          },
          // Only country + postcode drive the JSRates zone; province/city are ignored.
          // No `no_cities` flag — with it JSRates ignores this postcode and uses the
          // visitor's IP location, which is why the estimate disagreed with checkout.
          destination: {
            country: this.destCountry,
            postal_code: this.postcode,
            province: '',
            city: ''
          },
          items: this.getRateItems(),
          currency: this.currency,
          locale: this.dataset.locale || 'en'
        }
      };
    }

    /**
     * Items for the rate request. On the cart page a <script data-ea-cart-items> holds the
     * real line items (name/sku/qty/grams/price/ids); elsewhere (product page) it falls back
     * to the single current-variant from the data-* attributes.
     * @returns {Array}
     */
    getRateItems() {
      const el = this.querySelector('script[data-ea-cart-items]');
      if (el) {
        try {
          const arr = JSON.parse(el.textContent);
          if (Array.isArray(arr) && arr.length) return arr;
        } catch (e) { /* fall through to single item */ }
      }
      return [
        {
          name: this.dataset.variantName || '',
          sku: this.dataset.variantSku || '',
          quantity: 1,
          grams: parseInt(this.dataset.variantGrams, 10) || 0,
          price: parseInt(this.dataset.variantPrice, 10) || 0,
          requires_shipping: true,
          product_id: parseInt(this.dataset.productId, 10) || 0,
          variant_id: parseInt(this.dataset.variantId, 10) || 0
        }
      ];
    }

    getCacheKey() {
      return 'ea-rates-' + (this.dataset.variantId || '') + '-' + (this.postcode || '') + '-' + (this.destCountry || '');
    }

    async fetchRates(skipPrefetch) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this.hidden = false;

      // No postcode yet → prompt for one instead of guessing the location.
      if (!this.postcode) {
        this.renderNoRates();
        return;
      }

      // Try prefetched data or sessionStorage cache on initial load
      if (!skipPrefetch) {
        const cacheKey = this.getCacheKey();

        if (window.__eaPrefetch) {
          if (window.__eaPrefetch.data && window.__eaPrefetch.key === cacheKey) {
            const data = window.__eaPrefetch.data;
            if (data.rates && data.rates.length > 0) {
              this.rates = data.rates;
              this.selectedRateIndex = 0;
              this.renderResult();
              return;
            }
          }
          if (window.__eaPrefetch.promise && !window.__eaPrefetch.data) {
            this.renderLoading();
            try {
              const data = await window.__eaPrefetch.promise;
              if (data && data.rates && data.rates.length > 0) {
                this.rates = data.rates;
                this.selectedRateIndex = 0;
                this.renderResult();
                return;
              }
            } catch (e) { /* fall through to fresh fetch */ }
          }
        }

        // Check sessionStorage cache
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const data = JSON.parse(cached);
            if (data.rates && data.rates.length > 0) {
              this.rates = data.rates;
              this.selectedRateIndex = 0;
              this.renderResult();
              return;
            }
          }
        } catch (e) { /* fall through */ }
      }

      this.renderLoading();

      const headers = { 'Content-Type': 'application/json' };
      if (this.ratesKey) headers['x-jsrates-api-key'] = this.ratesKey;
      if (this.shopDomain) headers['x-jsrates-shop-domain'] = this.shopDomain;

      try {
        const response = await fetch(this.ratesUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(this.buildPayload()),
          signal: this.abortController.signal
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();

        if (!data.rates || data.rates.length === 0) {
          this.handleError('No rates returned');
          return;
        }

        this.rates = data.rates;
        this.selectedRateIndex = 0;
        this.renderResult();

        try {
          sessionStorage.setItem(this.getCacheKey(), JSON.stringify(data));
        } catch (e) { /* storage full */ }
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.handleError(err.message);
      }
    }

    renderLoading() {
      this.contentEl.innerHTML = `
        <div class="estimated-arrival__loading">
          <span class="estimated-arrival__spinner"></span>
          Checking delivery&hellip;
        </div>
      `;
    }

    renderResult() {
      const postcodeDisplay = this.postcode || '&hellip;';
      const truckSvg = `<svg class="estimated-arrival__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>`;

      let html = `
        <div class="estimated-arrival__result">
          ${truckSvg}
          <span class="estimated-arrival__label">Get it by —</span>
          <span class="estimated-arrival__location">${this.esc(this.getCountryName(this.destCountry))}, ${postcodeDisplay}</span>
          <button class="estimated-arrival__edit-btn" type="button" aria-label="Edit postcode" title="Edit postcode">&#9998;</button>
        </div>
        <div class="estimated-arrival__rates-list">
          ${this.rates.map((r) => {
            const price = r.total_price ? '$' + (parseInt(r.total_price, 10) / 100).toFixed(2) : '';
            const businessDays = this.getBusinessDays(r);
            const description = r.description || this.formatDeliveryDate(r);
            return `
              <div class="estimated-arrival__rate-item">
                <div class="estimated-arrival__rate-header">
                  <span class="estimated-arrival__rate-name">${this.esc(r.service_name || '')}</span>
                  ${price ? `<span class="estimated-arrival__rate-price">${price}</span>` : ''}
                </div>
                ${businessDays ? `<span class="estimated-arrival__rate-days">${businessDays}</span>` : ''}
                <span class="estimated-arrival__rate-desc">${this.esc(description)}</span>
              </div>
            `;
          }).join('')}
        </div>
      `;

      this.contentEl.innerHTML = html;

      this.contentEl.querySelector('.estimated-arrival__edit-btn')
        ?.addEventListener('click', () => this.renderEditForm());
    }

    renderEditForm() {
      this.contentEl.innerHTML = `
        <div class="estimated-arrival__edit-form">
          <label class="estimated-arrival__edit-label">
            Postcode
            <input type="text" class="estimated-arrival__input" value="${this.esc(this.postcode)}" placeholder="e.g. 2000" maxlength="10" autocomplete="postal-code" inputmode="numeric" />
          </label>
          <button type="button" class="estimated-arrival__check-btn">Check</button>
          <button type="button" class="estimated-arrival__cancel-btn">Cancel</button>
        </div>
      `;

      const input = this.contentEl.querySelector('.estimated-arrival__input');
      const checkBtn = this.contentEl.querySelector('.estimated-arrival__check-btn');
      const cancelBtn = this.contentEl.querySelector('.estimated-arrival__cancel-btn');

      input.focus();
      input.select();

      const submit = () => {
        const val = input.value.trim();
        if (val) {
          this.postcode = val;
          sessionStorage.setItem('ea-postcode', val);
          this.fetchRates(true);
        }
      };

      checkBtn.addEventListener('click', submit);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); submit(); }
        if (e.key === 'Escape') cancel();
      });
      const cancel = () => {
        // Clear cached data for a fresh state
        this.postcode = '';
        this.rates = [];
        this.destCountry = this.dataset.country || 'AU';
        try {
          sessionStorage.removeItem('ea-postcode');
          sessionStorage.removeItem('ea-visitor-location'); // legacy IP-location cache
          sessionStorage.removeItem(this.getCacheKey());
        } catch (e) { /* ignore */ }
        window.__eaPrefetch = null;
        this.renderNoRates();
      };
      cancelBtn.addEventListener('click', cancel);
    }

    handleError(msg) {
      console.error('[EstimatedArrival]', msg);
      this.hidden = false;
      this.renderNoRates();
    }

    renderNoRates() {
      const postcodeDisplay = this.postcode || '&hellip;';
      const truckSvg = `<svg class="estimated-arrival__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>`;

      this.contentEl.innerHTML = `
        <div class="estimated-arrival__result">
          ${truckSvg}
          <span class="estimated-arrival__label">Get it by —</span>
          <span class="estimated-arrival__location">${this.esc(this.getCountryName(this.destCountry))}, ${postcodeDisplay}</span>
          <button class="estimated-arrival__edit-btn" type="button" aria-label="Edit postcode" title="Edit postcode">&#9998;</button>
          <span class="estimated-arrival__date">&ndash; <button type="button" class="estimated-arrival__enter-postcode">Enter postcode</button></span>
        </div>
      `;

      this.contentEl.querySelector('.estimated-arrival__edit-btn')
        ?.addEventListener('click', () => this.renderEditForm());
      this.contentEl.querySelector('.estimated-arrival__enter-postcode')
        ?.addEventListener('click', () => this.renderEditForm());
    }

    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const variant = evt.detail.variant;
        const info = this.variantData.find(v => v.id === variant.id);
        if (info) {
          this.dataset.variantId = info.id;
          this.dataset.variantGrams = info.weight;
          this.dataset.variantPrice = info.price;
          this.dataset.variantSku = info.sku || '';
          this.dataset.variantName = info.title || '';
          this.dataset.productRequiresShipping = info.requires_shipping;

          if (info.requires_shipping === false) {
            this.hidden = true;
            return;
          }
        }
        this.fetchRates(true);
      } else {
        this.hidden = true;
      }
    }

    parseLocalDate(str) {
      const parts = String(str).split('T')[0].split('-').map(Number);
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }

    getBusinessDays(rate) {
      const maxField = rate.max_delivery_date || rate.min_delivery_date;
      const minField = rate.min_delivery_date;
      if (!maxField) return '';

      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const countBizDays = (target) => {
          const d = this.parseLocalDate(target);
          if (isNaN(d.getTime())) return null;
          let count = 0;
          const cur = new Date(today);
          while (cur < d) {
            cur.setDate(cur.getDate() + 1);
            const day = cur.getDay();
            if (day !== 0 && day !== 6) count++;
          }
          return Math.max(count, 1);
        };

        const minDays = minField ? countBizDays(minField) : null;
        const maxDays = countBizDays(maxField);

        if (maxDays === null) return '';
        if (minDays !== null && minDays !== maxDays) {
          return `${minDays} to ${maxDays} business days`;
        }
        return `${maxDays} business day${maxDays !== 1 ? 's' : ''}`;
      } catch (e) {
        return '';
      }
    }

    formatDeliveryDate(rate) {
      const minField = rate.min_delivery_date;
      const maxField = rate.max_delivery_date;

      if (!minField && !maxField) return rate.service_name || 'See checkout';

      try {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const fmt = (d) => `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;

        const minDate = minField ? this.parseLocalDate(minField) : null;
        const maxDate = maxField ? this.parseLocalDate(maxField) : null;

        if (minDate && isNaN(minDate.getTime())) return maxDate ? fmt(maxDate) : 'See checkout';
        if (maxDate && isNaN(maxDate.getTime())) return minDate ? fmt(minDate) : 'See checkout';

        if (minDate && maxDate) {
          if (minDate.getTime() === maxDate.getTime()) return fmt(minDate);
          return `${fmt(minDate)} - ${fmt(maxDate)}`;
        }

        return fmt(minDate || maxDate);
      } catch (e) {
        return rate.service_name || 'See checkout';
      }
    }

    getCountryName(code) {
      return CountriesObject[code] || code;
    }

    esc(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  customElements.define('estimated-arrival', EstimatedArrival);
}
